// Client mirror of the API's repeat-enrollment limit rule
// (src/api/enrollments/enrollment_limit.js) — "max N enrollments per X
// years". Three course columns drive it:
//
//   allow_repeat_enrollment = 0 -> hard cap of 1, ever
//   allow_repeat_enrollment = 1 -> max_enrollments NULL means unlimited;
//     otherwise at most max_enrollments non-deleted enrollments, counted
//     within a rolling enrollment_window_years window on created_at
//     (window NULL = lifetime).
//
// Every enrollment counts regardless of status (active, disabled, completed,
// expired). These pre-checks only shape the UI — the API re-validates at
// enrollment-intent, register, and the bundle endpoints.

// {max, windowYears|null} or null when unlimited
export function enrollmentLimitFor(course) {
    if (!course) return null;
    if (!course.allow_repeat_enrollment) return {max: 1, windowYears: null};
    const max = parseInt(course.max_enrollments);
    if (!max || max < 1) return null;
    const windowYears = parseInt(course.enrollment_window_years);
    return {max, windowYears: windowYears > 0 ? windowYears : null};
}

// The user's enrollments in a course that count against its limit, newest
// first. Rows come from the enrollments store (GET app/enrollments/), which
// already excludes deleted enrollments and carries courses.id + created_at.
export function countedEnrollments(enrollments, courseId, windowYears) {
    const cutoff = windowYears ? (() => {
        const d = new Date();
        d.setFullYear(d.getFullYear() - windowYears);
        return d;
    })() : null;
    return (enrollments || [])
        .filter(e => e.courses && e.courses.id === parseInt(courseId))
        .filter(e => !cutoff || new Date(e.created_at) >= cutoff)
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
}

// Can `adding` more enrollment(s) be taken right now? Returns
// {blocked, count, max, windowYears, nextEligibleOn|null} or null (unlimited).
export function enrollmentLimitStatus(course, enrollments, adding = 1) {
    const limit = enrollmentLimitFor(course);
    if (!limit) return null;
    const counted = countedEnrollments(enrollments, course.id, limit.windowYears);
    const blocked = counted.length + adding > limit.max;

    // With a rolling window, the request fits once the (max - adding + 1)-th
    // newest in-window enrollment ages out of the window.
    let nextEligibleOn = null;
    if (blocked && limit.windowYears) {
        const slot = counted[limit.max - adding];
        if (slot) {
            const d = new Date(slot.created_at);
            d.setFullYear(d.getFullYear() + limit.windowYears);
            nextEligibleOn = d;
        }
    }
    return {blocked, count: counted.length, max: limit.max, windowYears: limit.windowYears, nextEligibleOn};
}
